import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronRight,
  Command,
  LogOut,
  User as UserIcon,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { HealthPill } from '@/components/HealthPill';
import { systemApi } from '@/api/system';
import { useAuth, useLogout } from '@/hooks/useAuth';
import { useUiStore } from '@/stores/uiStore';

/**
 * TopBar — breadcrumbs, health pill, ⌘K, user menu. The health pill polls
 * `/api/system/health` on a 30s cadence. Errors collapse to "unknown"
 * so a flaky backend doesn't spam the user with toasts.
 */
export function TopBar() {
  const location = useLocation();
  const { user } = useAuth();
  const logout = useLogout();
  const toggleCommandPalette = useUiStore((s) => s.toggleCommandPalette);

  const healthQuery = useQuery({
    queryKey: ['system', 'health'],
    queryFn: systemApi.health,
    refetchInterval: 30_000,
    staleTime: 10_000,
    retry: false,
  });

  const crumbs = buildCrumbs(location.pathname);

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-4 border-b border-border bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1">
        <ol className="flex min-w-0 items-center gap-1 text-sm text-muted-foreground">
          {crumbs.map((c, i) => {
            const isLast = i === crumbs.length - 1;
            return (
              <li key={`${c.to}-${i}`} className="flex min-w-0 items-center gap-1">
                {i > 0 ? (
                  <ChevronRight
                    className="h-3.5 w-3.5 text-muted-foreground/60"
                    aria-hidden
                  />
                ) : null}
                {isLast ? (
                  <span className="truncate font-medium text-foreground">
                    {c.label}
                  </span>
                ) : (
                  <Link
                    to={c.to}
                    className="truncate hover:text-foreground"
                  >
                    {c.label}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      <div className="flex items-center gap-2">
        <HealthPill
          status={
            healthQuery.isLoading
              ? 'loading'
              : healthQuery.data?.status ?? 'down'
          }
          label={
            healthQuery.isLoading
              ? 'Checking…'
              : healthQuery.data
                ? undefined
                : 'Unknown'
          }
          className="hidden sm:inline-flex"
        />

        <Button
          variant="outline"
          size="sm"
          className="hidden h-8 gap-1.5 px-2 text-xs text-muted-foreground md:inline-flex"
          onClick={toggleCommandPalette}
          aria-label="Open command palette"
        >
          <Command className="h-3.5 w-3.5" aria-hidden />
          <span>Command</span>
          <kbd className="ml-1 rounded border border-border bg-muted px-1 font-mono text-[10px]">
            ⌘K
          </kbd>
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-md"
              aria-label="User menu"
            >
              <UserIcon className="h-4 w-4" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="flex flex-col">
              <span className="text-xs text-muted-foreground">Signed in as</span>
              <span className="truncate">{user?.username ?? '—'}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings">Settings</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => logout.mutate()}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="h-4 w-4" aria-hidden />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

interface Crumb {
  label: string;
  to: string;
}

/**
 * Derive breadcrumbs from the current pathname using a static label map.
 * Subagent D will extend this once dynamic segments (agent names, memory
 * filenames) need proper titles.
 */
function buildCrumbs(pathname: string): Crumb[] {
  if (pathname === '/' || pathname === '') {
    return [{ label: 'Dashboard', to: '/' }];
  }
  const parts = pathname.split('/').filter(Boolean);
  const crumbs: Crumb[] = [{ label: 'Dashboard', to: '/' }];
  let acc = '';
  for (const part of parts) {
    acc += `/${part}`;
    crumbs.push({ label: humanize(part), to: acc });
  }
  return crumbs;
}

function humanize(segment: string): string {
  if (!segment) return '';
  if (segment === 'new') return 'New';
  return segment
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
